import { Button, TextButton } from '@creditornot/cb-button';
import { ImageAdd } from '@creditornot/cb-icons';
import { Modal } from '@creditornot/cbt-modal';
import React, { useCallback, useMemo } from 'react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { ConfirmationModal } from 'modules/shared-menu/pages/ListingManagerImageGalleryPage/components/ImageGalleryAddToOffers/ConfirmationModal';
import { ImageGalleryAddToOffers } from 'modules/shared-menu/pages/ListingManagerImageGalleryPage/components/ImageGalleryAddToOffers/ImageGalleryAddToOffers';
import { ImageGalleryHeader } from 'modules/shared-menu/pages/ListingManagerImageGalleryPage/components/ImageGalleryHeader';
import { useOfferDraftChanges } from 'modules/shared-menu/pages/ListingManagerImageGalleryPage/useOfferDraftChanges';
import { useOffersMutation } from 'modules/shared-menu/pages/ListingManagerImageGalleryPage/useOffersMutation';

import ActionButton from '../ActionButton';

type BulkAttachToImagesProps = {
  selectedIds: Set<string>;
};

const HeaderContentContainer = styled.div`
  display: flex;
  gap: 10px;
`;

export const BulkAttachToImages = ({ selectedIds }: BulkAttachToImagesProps) => {
  const intl = useIntl();
  const [showModal, setShowModal] = useState(false);
  const [isConfirmDiscardChangesModalOpen, setIsConfirmDiscardChangesModalOpen] = useState(false);
  const menuPermissions = useOfferingMenuPermissionsContext();
  const { offers } = useSelectionData();
  const offersToUpdate = offers.filter(offer => selectedIds.has(offer.id));
  const { updateOfferDraftChanges, offerDraftChanges, offersWithDraftChanges, resetDraftChanges } =
    useOfferDraftChanges();
  const { isLoading: isUpdatingOffers, mutate: updateOffers } = useOffersMutation();
  const changedOffersCount = useMemo(() => Object.keys(offerDraftChanges).length, [offerDraftChanges]);

  const onClose = useCallback(() => {
    resetDraftChanges();
    setIsConfirmDiscardChangesModalOpen(false);
    setShowModal(false);
  }, [resetDraftChanges, setShowModal]);

  const handleClose = useCallback(() => {
    if (changedOffersCount > 0) {
      setIsConfirmDiscardChangesModalOpen(true);
      return;
    }
    onClose();
  }, [changedOffersCount, setIsConfirmDiscardChangesModalOpen, onClose]);

  const updateChangedOffers = useCallback(() => {
    updateOffers({
      offerDraftChanges,
      onSuccess: () => {
        onClose();
      },
    });
  }, [offerDraftChanges, updateOffers, onClose]);

  return (
    <>
      <ActionButton
        disabled={!menuPermissions.contentEdit}
        icon={<ImageAdd />}
        onClick={() => setShowModal(true)}
        tooltipTitle={<FormattedMessage id="menu.bulkActions.attachToImages.attachToImages" />}
      />
      <Modal open={showModal} onDismiss={handleClose} sizePreset="fullScreen" disableContentPadding>
        <ImageGalleryAddToOffers
          onClose={handleClose}
          offersIds={offersToUpdate.map(offer => offer.id)}
          updateOfferDraftChanges={updateOfferDraftChanges}
          offerDraftChanges={offerDraftChanges}
          offersWithDraftChanges={offersWithDraftChanges}
          showUploader
          showInfobox
          headerContent={
            <ImageGalleryHeader
              title={intl.formatMessage({ id: 'shared-menu.editor.image-gallery.add-to-items.title' })}
            >
              <HeaderContentContainer>
                <TextButton onClick={handleClose}>
                  <FormattedMessage id="shared-menu.editor.image-gallery.add-to-items.close" />
                </TextButton>
                <Button
                  variant="primary"
                  size="small"
                  disabled={isUpdatingOffers || changedOffersCount === 0}
                  onClick={updateChangedOffers}
                >
                  {isUpdatingOffers ? (
                    <FormattedMessage id="shared-menu.editor.image-gallery.add-to-items.saving" />
                  ) : (
                    <FormattedMessage id="shared-menu.editor.image-gallery.add-to-items.save" />
                  )}
                </Button>
              </HeaderContentContainer>
            </ImageGalleryHeader>
          }
        />
        <ConfirmationModal
          open={isConfirmDiscardChangesModalOpen}
          onCancel={() => setIsConfirmDiscardChangesModalOpen(false)}
          onConfirm={onClose}
        />
      </Modal>
    </>
  );
};
